import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Chip, Container, Stack } from '@mui/material'
import useSessionStorage from '@/hooks/useSessionStorage'
import { useGetProject, useGetProjects } from '@/service-library/hooks/projects'
import { useGetWorkflows } from '@/service-library/hooks/workflows'
import WorkflowLiteProvider from '@/components/lite/document-viewer/idle-components/WorkflowLiteProvider'
import DocumentsTable from '@/components/lite/DocumentsTable'
import UploadBox from '@/components/lite/UploadBox'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'
import ProjectProvider from '@/components/project-tables/ProjectProvider'
import FileUploadProvider from '@/components/upload-files/FileUploadProvider'

export default function MainPage() {
  const { rootOrganization, setRootOrganizationById } = useRootOrganization()
  const [searchParams] = useSearchParams()
  const { projects, isPending } = useGetProjects({
    filters: {
      limit: '1000',
      org_id: rootOrganization?.id,
    },
  })
  const [selectedProjectId, setSelectedProjectId] = useSessionStorage(
    'lite-project-id',
    projects[0]?.id,
  )

  const { project, queryKey } = useGetProject({
    id: selectedProjectId,
    filters: {
      fields__include: 'project_grids',
      project_grids__fields__include:
        'project_grid_fields,sub_project_grid_fields',
      project_grids__project_grid_fields__fields__include:
        'project_grid_field_type,project_grid_id,included_grid_fields,included_grid_fields_ids,project_grid_field_rules,name,has_category_model,project_model_id',
      project_grids__project_grid_fields__project_grid_field_rules__fields__include:
        'rule',
    },
    enabled: !!selectedProjectId,
  })

  const { workflows } = useGetWorkflows({
    filters: {
      limit: '1',
      project_id: selectedProjectId,
      code: 'basic',
      fields__include: 'workflow_states',
      workflow_states__fields__include: 'workflow_id',
    },
    enabled: !!selectedProjectId,
  })

  useEffect(() => {
    if (!selectedProjectId && !isPending) {
      projects.length && setSelectedProjectId(projects[0].id)
    } else if (
      selectedProjectId &&
      project &&
      rootOrganization &&
      project.org_id !== rootOrganization.id
    ) {
      if (searchParams.has('document_id')) {
        setRootOrganizationById(project.org_id)
      } else {
        if (projects.length) {
          setSelectedProjectId(projects[0].id)
        } else {
          setSelectedProjectId('')
        }
      }
    }
  }, [
    isPending,
    project,
    projects,
    rootOrganization,
    rootOrganization.id,
    searchParams,
    selectedProjectId,
    setRootOrganizationById,
    setSelectedProjectId,
  ])

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="center">
          {projects.map(({ id, name }) => {
            return (
              <Chip
                color="primary"
                variant={project?.id === id ? 'filled' : 'outlined'}
                key={id}
                label={name}
                onClick={() => {
                  setSelectedProjectId(id)
                }}
              />
            )
          })}
        </Stack>
        {project && (
          <Stack spacing={3}>
            <FileUploadProvider project={project} workflowId={workflows[0]?.id}>
              <UploadBox />
            </FileUploadProvider>
            <WorkflowLiteProvider workflow={workflows[0]}>
              <ProjectProvider project={project} queryKey={queryKey}>
                <DocumentsTable
                  project={project}
                  workflowId={workflows[0]?.id}
                />
              </ProjectProvider>
            </WorkflowLiteProvider>
          </Stack>
        )}
      </Stack>
    </Container>
  )
}
